import React, { useState, useEffect } from 'react';
import { CreditCard, DollarSign, TrendingUp, AlertCircle, CheckCircle, Clock, XCircle, RefreshCw, Wallet, Receipt, Calendar } from 'lucide-react';
import axios from 'axios';
import { api_url_v1 } from '../utils/config';

const Transactions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchTransactionSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${api_url_v1}/getTransactionSummary`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });
      setData(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch transaction summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactionSummary();
  }, []);

  const formatCurrency = (amount) => {
    return `£${(amount / 100).toFixed(2)}`;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      succeeded: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Succeeded' },
      requires_payment_method: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Pending' },
      pending: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Pending' },
      failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Failed' }
    };
    return statusMap[status] || statusMap.pending;
  };

  const calculateStats = () => {
    if (!data) return null;

    const paymentIntents = data.payment_intents?.items || [];
    const charges = data.charges?.items || [];
    
    const succeededIntents = paymentIntents.filter(pi => pi.status === 'succeeded');
    const pendingIntents = paymentIntents.filter(pi => pi.status === 'requires_payment_method');
    
    const totalRevenue = succeededIntents.reduce((sum, pi) => sum + pi.amount, 0);
    const pendingAmount = pendingIntents.reduce((sum, pi) => sum + pi.amount, 0);
    
    const availableBalance = data.balance?.available?.[0]?.amount || 0;
    const pendingBalance = data.balance?.pending?.[0]?.amount || 0;

    return {
      totalTransactions: paymentIntents.length,
      successfulTransactions: succeededIntents.length,
      pendingTransactions: pendingIntents.length,
      totalRevenue,
      pendingAmount,
      availableBalance,
      pendingBalance,
      averageTransaction: succeededIntents.length > 0 ? totalRevenue / succeededIntents.length : 0,
      successRate: paymentIntents.length > 0 ? (succeededIntents.length / paymentIntents.length * 100).toFixed(1) : 0
    };
  };

  const stats = calculateStats();

  const StatCard = ({ title, value, icon: Icon, color = "green", subtitle }) => {
    const colorClasses = {
      green: 'bg-green-100 text-green-600',
      blue: 'bg-blue-100 text-blue-600',
      purple: 'bg-purple-100 text-purple-600',
      amber: 'bg-amber-100 text-amber-600'
    };

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon size={24} />
          </div>
        </div>
        <h3 className="text-gray-600 text-sm font-medium mb-1">{title}</h3>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading transaction data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto mt-20">
          <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="text-red-600" size={32} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Transactions</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={fetchTransactionSummary}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors inline-flex items-center space-x-2"
            >
              <RefreshCw size={18} />
              <span>Try Again</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Transaction Overview</h1>
            <p className="text-gray-600">Stripe payment transactions and balance summary</p>
          </div>
          <button
            onClick={fetchTransactionSummary}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center space-x-2"
          >
            <RefreshCw size={18} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Wallet size={24} />
                <h3 className="text-lg font-semibold">Available Balance</h3>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${data?.balance?.livemode ? 'bg-green-700' : 'bg-amber-700'}`}>
                {data?.balance?.livemode ? 'Live' : 'Test'}
              </span>
            </div>
            <p className="text-4xl font-bold mb-2">{formatCurrency(stats?.availableBalance)}</p>
            <p className="text-green-100 text-sm">Ready for payout</p>
          </div>

          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center space-x-2 mb-4">
              <Clock size={24} />
              <h3 className="text-lg font-semibold">Pending Balance</h3>
            </div>
            <p className="text-4xl font-bold mb-2">{formatCurrency(stats?.pendingBalance)}</p>
            <p className="text-amber-100 text-sm">Being processed</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Revenue"
            value={formatCurrency(stats?.totalRevenue)}
            subtitle="From successful payments"
            icon={DollarSign}
            color="green"
          />
          <StatCard
            title="Total Transactions"
            value={stats?.totalTransactions}
            subtitle={`${stats?.successRate}% success rate`}
            icon={Receipt}
            color="blue"
          />
          <StatCard
            title="Successful Payments"
            value={stats?.successfulTransactions}
            icon={CheckCircle}
            color="green"
          />
          <StatCard
            title="Pending Payments"
            value={stats?.pendingTransactions}
            subtitle={formatCurrency(stats?.pendingAmount)}
            icon={Clock}
            color="amber"
          />
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <StatCard
            title="Average Transaction"
            value={formatCurrency(stats?.averageTransaction)}
            subtitle="Per successful payment"
            icon={TrendingUp}
            color="purple"
          />
          <StatCard
            title="Total Charges"
            value={data?.charges?.items?.length || 0}
            subtitle="Completed charges"
            icon={CreditCard}
            color="blue"
          />
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {['overview', 'payment-intents', 'charges'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? 'border-green-600 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Payment Intents Table */}
        {activeTab === 'payment-intents' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Payment Intents</h2>
              <p className="text-sm text-gray-600 mt-1">All payment intent records</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.payment_intents?.items?.map((intent) => {
                    const statusInfo = getStatusInfo(intent.status);
                    return (
                      <tr key={intent.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-gray-900">{intent.id}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-semibold text-gray-900">{formatCurrency(intent.amount)}</span>
                          <span className="text-xs text-gray-500 ml-2 uppercase">{intent.currency}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <statusInfo.icon className={statusInfo.color} size={18} />
                            <span className={`text-sm ${statusInfo.color}`}>{statusInfo.label}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(intent.created)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Charges Table */}
        {activeTab === 'charges' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Charges</h2>
              <p className="text-sm text-gray-600 mt-1">Completed payment charges</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Card</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Receipt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.charges?.items?.map((charge) => (
                    <tr key={charge.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono text-gray-900">{charge.id}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(charge.amount)}</span>
                        <span className="text-xs text-gray-500 ml-2 uppercase">{charge.currency}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <CreditCard size={16} className="text-gray-400" />
                          <span className="text-sm text-gray-900 capitalize">{charge.card?.brand}</span>
                          <span className="text-sm text-gray-500">••{charge.card?.last4}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          charge.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {charge.paid ? 'Paid' : 'Unpaid'}
                          {charge.refunded && ' (Refunded)'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(charge.created)}
                      </td>
                      <td className="px-6 py-4">
                        {charge.receipt_url && (
                          <a
                            href={charge.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:text-green-700 text-sm font-medium"
                          >
                            View
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Overview Summary */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Payment Intents */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Recent Payment Intents</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {data?.payment_intents?.items?.slice(0, 5).map((intent) => {
                    const statusInfo = getStatusInfo(intent.status);
                    return (
                      <div key={intent.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <statusInfo.icon className={statusInfo.color} size={20} />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{formatCurrency(intent.amount)}</p>
                            <p className="text-xs text-gray-500">{formatDate(intent.created)}</p>
                          </div>
                        </div>
                        <span className={`text-xs font-medium ${statusInfo.color}`}>{statusInfo.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Recent Charges */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Recent Charges</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {data?.charges?.items?.slice(0, 5).map((charge) => (
                    <div key={charge.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <CreditCard className="text-green-600" size={20} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{formatCurrency(charge.amount)}</p>
                          <p className="text-xs text-gray-500 capitalize">{charge.card?.brand} ••{charge.card?.last4}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        charge.paid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {charge.paid ? 'Paid' : 'Unpaid'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Transactions;